import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  BadgeCheck,
  Banknote,
  CalendarRange,
  CheckCircle2,
  ClipboardList,
  Clock3,
  PackageCheck,
  RefreshCw,
  Sparkles,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingIndicator from '../components/LoadingIndicator';
import UserSectionSidebar from '../components/UserSectionSidebar';
import { useTranslations } from '../translations';
import type { SellerOrderReport, SellerOrderSummary } from '../services/api.types';
import { fetchSellerOrderReport } from '../services/seller-order.api';
import type { SellerOrderReportFilters } from '../services/seller-order.api';
import { localDateValue, toEndOfDayInstant, toStartOfDayInstant } from '../utils/dateRange';
import type { AccountPageProps } from './accountPage.types';
import './OrderStatistics.css';

type StatisticsPeriod = 30 | 90 | 365;

interface StatisticsData {
  trend: SellerOrderReport;
  products: SellerOrderReport;
  previous: SellerOrderReport;
}

interface MetricCardProps {
  label: string;
  value: string;
  hint: React.ReactNode;
  icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
  tone?: 'primary' | 'success' | 'neutral';
}

const numberFormatter = new Intl.NumberFormat('ro-RO');
const moneyFormatter = new Intl.NumberFormat('ro-RO', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const money = (value: number) => `${moneyFormatter.format(value || 0)} lei`;
const percentage = (value: number) => `${numberFormatter.format(Math.round(value))}%`;

const getPeriodDates = (period: StatisticsPeriod) => {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - period + 1);
  const previousEnd = new Date(start);
  previousEnd.setDate(previousEnd.getDate() - 1);
  const previousStart = new Date(previousEnd);
  previousStart.setDate(previousStart.getDate() - period + 1);

  return {
    from: localDateValue(start),
    to: localDateValue(end),
    previousFrom: localDateValue(previousStart),
    previousTo: localDateValue(previousEnd),
  };
};

const periodGranularity = (period: StatisticsPeriod) => {
  if (period === 30) return 'DAY' as const;
  if (period === 90) return 'WEEK' as const;
  return 'MONTH' as const;
};

const changeFrom = (current: number, previous: number): number | null => {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
};

const MetricCard: React.FC<MetricCardProps> = ({ label, value, hint, icon: Icon, tone = 'neutral' }) => (
  <article className={`order-stat-metric order-stat-metric-${tone}`}>
    <span className="order-stat-metric-icon"><Icon size={20} aria-hidden /></span>
    <div className="order-stat-metric-copy">
      <span>{label}</span>
      <strong>{value}</strong>
      <div>{hint}</div>
    </div>
  </article>
);

const OrderStatistics: React.FC<AccountPageProps> = ({
  isLoggedIn,
  isAuthChecking,
  isAdmin = false,
  onNavigateToLogin,
  onNavigateToRegister,
  onNavigateToHome,
  onNavigateToProfile,
  onNavigateToLocations,
  onNavigateToMyAnnouncements,
  onNavigateToSettings,
  onNavigateToAdministration,
  onLogout,
  onNavigateToCreateProduct,
  onNavigateToMessages,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToCookies,
  onNavigateToAbout,
  onNavigateToContact,
}) => {
  const t = useTranslations();
  const copy = t.orderStatistics;
  const [period, setPeriod] = useState<StatisticsPeriod>(30);
  const [data, setData] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (isAuthChecking) return;
    if (!isLoggedIn || isAdmin) {
      onNavigateToLogin();
      return;
    }

    let active = true;
    const range = getPeriodDates(period);
    const commonFilters = {
      granularity: periodGranularity(period),
      dateField: 'ORDER_DATE' as const,
      statuses: [],
    };
    const currentFilters: SellerOrderReportFilters = {
      ...commonFilters,
      from: toStartOfDayInstant(range.from),
      to: toEndOfDayInstant(range.to),
      breakdown: 'TOTAL',
    };
    const productFilters: SellerOrderReportFilters = {
      ...currentFilters,
      breakdown: 'PRODUCT',
    };
    const previousFilters: SellerOrderReportFilters = {
      ...commonFilters,
      from: toStartOfDayInstant(range.previousFrom),
      to: toEndOfDayInstant(range.previousTo),
      breakdown: 'TOTAL',
    };

    setLoading(true);
    setError(false);
    Promise.all([
      fetchSellerOrderReport(currentFilters),
      fetchSellerOrderReport(productFilters),
      fetchSellerOrderReport(previousFilters),
    ])
      .then(([trend, products, previous]) => {
        if (active) setData({ trend, products, previous });
      })
      .catch((loadError) => {
        console.error('Could not load seller order statistics', loadError);
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [isAdmin, isAuthChecking, isLoggedIn, onNavigateToLogin, period, reloadKey]);

  const summary = data?.trend.summary;
  const previousSummary = data?.previous.summary;

  const chartData = useMemo(() => data?.trend.rows.map((row) => ({
    name: row.periodLabel,
    orders: row.orderCount,
    expected: row.expectedAmount,
    delivered: row.deliveredAmount,
  })) ?? [], [data]);

  const topProducts = useMemo(() => {
    const grouped = new Map<string, { id: string; title: string; orders: number; amount: number }>();
    data?.products.rows.forEach((row) => {
      const key = row.productId || row.productTitle || copy.unknownProduct;
      const current = grouped.get(key) || {
        id: key,
        title: row.productTitle || copy.unknownProduct,
        orders: 0,
        amount: 0,
      };
      current.orders += row.orderCount;
      current.amount += row.expectedAmount;
      grouped.set(key, current);
    });
    return Array.from(grouped.values())
      .sort((first, second) => second.amount - first.amount || second.orders - first.orders)
      .slice(0, 5);
  }, [copy.unknownProduct, data]);

  const comparisonHint = (current: number, previous: number, valueLabel: string) => {
    const change = changeFrom(current, previous);
    if (change === null) return <span className="order-stat-new"><Sparkles size={13} aria-hidden />{copy.newActivity}</span>;
    if (change === 0) return <span>{copy.noChange}</span>;
    const positive = change > 0;
    return (
      <span className={positive ? 'order-stat-positive' : 'order-stat-negative'}>
        {positive ? <ArrowUpRight size={14} aria-hidden /> : <ArrowDownRight size={14} aria-hidden />}
        {percentage(Math.abs(change))} {valueLabel}
      </span>
    );
  };

  const statusItems = summary ? [
    { label: copy.pending, value: summary.pendingCount, color: '#d99400' },
    { label: copy.delivered, value: summary.deliveredCount, color: '#267200' },
    { label: copy.cancelled, value: summary.cancelledCount, color: '#c65338' },
  ] : [];
  const completedOrders = (summary?.deliveredCount || 0) + (summary?.cancelledCount || 0);
  const deliveryRate = completedOrders ? ((summary?.deliveredCount || 0) / completedOrders) * 100 : 0;
  const cancellationRate = summary?.orderCount ? (summary.cancelledCount / summary.orderCount) * 100 : 0;
  const activeOrderCount = summary ? summary.orderCount - summary.cancelledCount : 0;
  const averageOrder = activeOrderCount && summary ? summary.expectedAmount / activeOrderCount : 0;
  const maximumProductAmount = topProducts[0]?.amount || 0;

  return (
    <div className="order-statistics-page">
      <Header
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
        onLogout={onLogout}
        onProfileClick={onNavigateToProfile}
        onLocationsClick={onNavigateToLocations}
        onMessagesClick={onNavigateToMessages}
        onAnnouncementsClick={onNavigateToMyAnnouncements}
        onAdministrationClick={onNavigateToAdministration}
        onSettingsClick={onNavigateToSettings}
        onNavigateToCreateProduct={onNavigateToCreateProduct}
      />

      <div className="order-statistics-container">
        <UserSectionSidebar
          activeItem="orderStatistics"
          onProfileClick={onNavigateToProfile}
          onLocationsClick={onNavigateToLocations}
          onMessagesClick={() => onNavigateToMessages?.()}
          onAnnouncementsClick={onNavigateToMyAnnouncements}
          onSettingsClick={onNavigateToSettings}
        />

        <main className="order-statistics-main">
          <header className="order-statistics-header">
            <div className="order-statistics-heading">
              <span className="order-statistics-eyebrow"><TrendingUp size={15} aria-hidden />{copy.eyebrow}</span>
              <h1>{copy.title}</h1>
              <p>{copy.subtitle}</p>
            </div>
            <div className="order-statistics-overview">
              <span><Banknote size={20} aria-hidden /></span>
              <div><strong>{money(summary?.expectedAmount || 0)}</strong><small>{copy.activeRevenue}</small></div>
            </div>
          </header>

          <section className="order-statistics-toolbar" aria-label={copy.periodLabel}>
            <div>
              <CalendarRange size={18} aria-hidden />
              <span>{copy.periodLabel}</span>
            </div>
            <div className="order-statistics-period" role="group" aria-label={copy.periodLabel}>
              {([
                [30, copy.last30Days],
                [90, copy.last90Days],
                [365, copy.lastYear],
              ] as Array<[StatisticsPeriod, string]>).map(([value, label]) => (
                <button
                  type="button"
                  key={value}
                  className={period === value ? 'active' : ''}
                  aria-pressed={period === value}
                  onClick={() => setPeriod(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          {error && !data ? (
            <section className="order-statistics-state order-statistics-error">
              <RefreshCw size={28} aria-hidden />
              <h2>{copy.loadError}</h2>
              <p>{copy.loadErrorHint}</p>
              <button type="button" onClick={() => setReloadKey((key) => key + 1)}>
                <RefreshCw size={17} aria-hidden />{copy.retry}
              </button>
            </section>
          ) : loading && !data ? (
            <LoadingIndicator message={copy.loading} className="order-statistics-state" />
          ) : summary && previousSummary ? (
            <>
              <section className="order-statistics-metrics" aria-label={copy.summaryLabel}>
                <MetricCard
                  label={copy.totalOrders}
                  value={numberFormatter.format(summary.orderCount)}
                  icon={ClipboardList}
                  tone="primary"
                  hint={comparisonHint(summary.orderCount, previousSummary.orderCount, copy.vsPreviousPeriod)}
                />
                <MetricCard
                  label={copy.deliveredOrders}
                  value={numberFormatter.format(summary.deliveredCount)}
                  icon={PackageCheck}
                  tone="success"
                  hint={<span>{percentage(deliveryRate)} {copy.completionRate}</span>}
                />
                <MetricCard
                  label={copy.activeRevenue}
                  value={money(summary.expectedAmount)}
                  icon={Banknote}
                  hint={comparisonHint(summary.expectedAmount, previousSummary.expectedAmount, copy.vsPreviousPeriod)}
                />
                <MetricCard
                  label={copy.averageOrder}
                  value={money(averageOrder)}
                  icon={BadgeCheck}
                  hint={<span>{copy.perOrder}</span>}
                />
              </section>

              <section className="order-statistics-grid">
                <article className="order-statistics-card order-statistics-trend-card">
                  <header className="order-statistics-card-header">
                    <div><h2>{copy.revenueTrend}</h2><p>{copy.revenueTrendHint}</p></div>
                    {loading && <RefreshCw className="order-statistics-refreshing" size={18} aria-label={copy.loading} />}
                  </header>
                  {chartData.length ? (
                    <div className="order-statistics-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} accessibilityLayer margin={{ top: 12, right: 8, bottom: 0, left: -18 }}>
                          <defs>
                            <linearGradient id="orderExpectedArea" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#267200" stopOpacity={0.24} />
                              <stop offset="100%" stopColor="#267200" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="orderDeliveredArea" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#7b9f35" stopOpacity={0.2} />
                              <stop offset="100%" stopColor="#7b9f35" stopOpacity={0.01} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid vertical={false} stroke="#e0e8dc" strokeDasharray="4 4" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#687364', fontSize: 11 }} minTickGap={28} />
                          <YAxis axisLine={false} tickLine={false} tick={{ fill: '#687364', fontSize: 11 }} tickFormatter={(value) => moneyFormatter.format(Number(value))} />
                          <Tooltip
                            formatter={(value: unknown, name: unknown) => [money(Number(value)), String(name)]}
                            contentStyle={{ borderRadius: 12, border: '1px solid #dbe5d6', boxShadow: '0 10px 30px rgba(30, 61, 20, .12)' }}
                          />
                          <Area type="monotone" dataKey="expected" name={copy.expectedRevenue} stroke="#267200" fill="url(#orderExpectedArea)" strokeWidth={2.5} />
                          <Area type="monotone" dataKey="delivered" name={copy.deliveredRevenue} stroke="#7b9f35" fill="url(#orderDeliveredArea)" strokeWidth={2.2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : <div className="order-statistics-empty">{copy.empty}</div>}
                </article>

                <article className="order-statistics-card order-statistics-status-card">
                  <header className="order-statistics-card-header"><div><h2>{copy.orderStatus}</h2><p>{copy.orderStatusHint}</p></div></header>
                  <div className="order-statistics-status-list">
                    {statusItems.map((item) => {
                      const share = summary.orderCount ? (item.value / summary.orderCount) * 100 : 0;
                      return (
                        <div className="order-statistics-status" key={item.label}>
                          <div><span><i style={{ background: item.color }} />{item.label}</span><strong>{item.value}<small>{percentage(share)}</small></strong></div>
                          <div className="order-statistics-progress" aria-hidden><span style={{ width: `${share}%`, background: item.color }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </article>

                <article className="order-statistics-card order-statistics-performance-card">
                  <header className="order-statistics-card-header"><div><h2>{copy.performance}</h2><p>{copy.performanceHint}</p></div></header>
                  <div className="order-statistics-performance-list">
                    <div><span className="success"><CheckCircle2 size={18} aria-hidden /></span><p>{copy.completionRate}<strong>{percentage(deliveryRate)}</strong></p></div>
                    <div><span className="warning"><XCircle size={18} aria-hidden /></span><p>{copy.cancellationRate}<strong>{percentage(cancellationRate)}</strong></p></div>
                    <div><span><Clock3 size={18} aria-hidden /></span><p>{copy.pendingOrders}<strong>{numberFormatter.format(summary.pendingCount)}</strong></p></div>
                  </div>
                </article>

                <article className="order-statistics-card order-statistics-products-card">
                  <header className="order-statistics-card-header"><div><h2>{copy.topProducts}</h2><p>{copy.topProductsHint}</p></div></header>
                  {topProducts.length ? (
                    <ol className="order-statistics-products">
                      {topProducts.map((product, index) => (
                        <li key={product.id}>
                          <span className="order-statistics-rank">{index + 1}</span>
                          <div className="order-statistics-product-copy">
                            <div><strong>{product.title}</strong><small>{copy.orderCount.replace('{count}', numberFormatter.format(product.orders))}</small></div>
                            <div className="order-statistics-product-progress" aria-hidden><span style={{ width: `${maximumProductAmount ? (product.amount / maximumProductAmount) * 100 : 0}%` }} /></div>
                          </div>
                          <strong className="order-statistics-product-amount">{money(product.amount)}</strong>
                        </li>
                      ))}
                    </ol>
                  ) : <div className="order-statistics-empty order-statistics-empty-compact">{copy.noProducts}</div>}
                </article>
              </section>
            </>
          ) : null}
        </main>
      </div>

      <Footer
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookies={onNavigateToCookies}
        onNavigateToAbout={onNavigateToAbout}
        onNavigateToContact={onNavigateToContact}
      />
    </div>
  );
};

export default OrderStatistics;
